import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

const adminUserSchema = z
  .object({
    id: z.number(),
    display_name: z.string(),
    first_name: nullableString,
    last_name: nullableString,
    email: nullableString,
    profile_image: nullableString,
  })
  .passthrough()

const adminMemberSchema = z
  .object({
    user_id: z.number(),
    status: z.string(),
    role: z.string(),
    joined_at: nullableString,
    rank: nullableNumber,
    total_points: z.number().optional().default(0),
    can_remove: z.boolean().optional().default(false),
    can_manage_role: z.boolean().optional().default(false),
    user: adminUserSchema.nullable(),
  })
  .passthrough()

const adminOverviewSchema = z
  .object({
    poule: z
      .object({
        id: z.number(),
        name: z.string(),
        audience_type: nullableString,
        description: nullableString,
        thumbnail_url: nullableString,
        hero_url: nullableString,
        visibility: nullableString,
        requires_approval: z.boolean().optional(),
        closed: z.boolean().optional(),
        supports_teams: z.boolean().optional(),
      })
      .passthrough(),
    membership: z
      .object({
        role: nullableString,
        status: nullableString,
      })
      .passthrough(),
    summary: z
      .object({
        accepted_members_count: z.number().optional().default(0),
        pending_members_count: z.number().optional().default(0),
        admins_count: z.number().optional().default(0),
        active_invites_count: z.number().optional().default(0),
      })
      .passthrough(),
    permissions: z
      .object({
        can_manage_members: z.boolean().optional().default(false),
        can_manage_settings: z.boolean().optional().default(false),
        can_manage_owner: z.boolean().optional().default(false),
        can_delete: z.boolean().optional().default(false),
        can_manage_communication: z.boolean().optional().default(false),
        can_manage_imports: z.boolean().optional().default(false),
      })
      .passthrough(),
    license: z
      .object({
        active: z.boolean().default(false),
        required: z.boolean().default(false),
        activation_status: z.enum(['pending', 'active']).optional().default('pending'),
        product_key: nullableString,
      })
      .passthrough()
      .default({ active: false, required: true }),
  })
  .passthrough()

const adminMembersResponseSchema = z
  .object({
    poule_id: z.number(),
    data: z.array(adminMemberSchema).default([]),
    current_page: z.number().optional().default(1),
    last_page: z.number().optional().default(1),
    total: z.number().optional().default(0),
  })
  .passthrough()

const communicationPreviewSchema = z
  .object({
    subject: z.string(),
    body: z.string(),
    locale: z.string(),
    greeting: z.string(),
    closing: z.string(),
    signature: z.string(),
    requested_count: z.number().default(0),
    eligible_count: z.number().default(0),
    skipped_count: z.number().default(0),
    skipped: z.record(z.string(), z.number()).default({}),
  })
  .passthrough()

const communicationSchema = z
  .object({
    id: z.number(),
    subject: z.string(),
    body: z.string(),
    locale: z.string(),
    target_scope: z.string(),
    status: z.string(),
    requested_count: z.number().default(0),
    queued_count: z.number().default(0),
    sent_count: z.number().default(0),
    skipped_count: z.number().default(0),
    failed_count: z.number().default(0),
    queued_at: nullableString,
    completed_at: nullableString,
    created_at: nullableString,
  })
  .passthrough()

const communicationsResponseSchema = z
  .object({
    poule_id: z.number(),
    data: z.array(communicationSchema).default([]),
    current_page: z.number().default(1),
    last_page: z.number().default(1),
    total: z.number().default(0),
  })
  .passthrough()

const adminSettingsSchema = z
  .object({
    poule_id: z.number(),
    name: z.string(),
    description: nullableString,
    visibility: z.string(),
    requires_approval: z.boolean(),
    prediction_release_mode: z.string(),
    show_in_overviews: z.boolean(),
    closed: z.boolean(),
  })
  .passthrough()

const auditEventSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    action: z.string(),
    subject_type: nullableString,
    subject_id: nullableNumber,
    metadata: z.record(z.string(), z.unknown()).default({}),
    created_at: nullableString,
    actor: adminUserSchema.nullable(),
  })
  .passthrough()

const auditResponseSchema = z
  .object({
    poule_id: z.number(),
    data: z.array(auditEventSchema).default([]),
    current_page: z.number().default(1),
    last_page: z.number().default(1),
    total: z.number().default(0),
  })
  .passthrough()

const importRowSchema = z
  .object({
    row_number: z.number(),
    user_id: nullableNumber,
    first_name: nullableString,
    last_name: nullableString,
    display_name: nullableString,
    email: nullableString,
    match_status: z.string(),
    commit_status: nullableString,
    error_message: nullableString,
  })
  .passthrough()

const memberImportSchema = z
  .object({
    id: z.number(),
    poule_id: z.number(),
    status: z.string(),
    original_filename: nullableString,
    columns: z.array(z.string()).default([]),
    total_rows: z.number().default(0),
    valid_rows: z.number().default(0),
    matched_rows: z.number().default(0),
    candidate_rows: z.number().default(0),
    error_rows: z.number().default(0),
    pending_members_created: z.number().default(0),
    committed_at: nullableString,
    created_at: nullableString,
    rows: z.array(importRowSchema).optional(),
  })
  .passthrough()

const correctionMatchSchema = z
  .object({
    match_id: z.number(),
    kickoff_at: nullableString,
    home_team: nullableString,
    away_team: nullableString,
    predictions_processed: z.boolean(),
    home_pred: nullableNumber,
    away_pred: nullableNumber,
  })
  .passthrough()

const correctionResponseSchema = z
  .object({
    poule_id: z.number(),
    user_id: z.number(),
    data: z.array(correctionMatchSchema).default([]),
  })
  .passthrough()

const inviteSchema = z
  .object({
    id: z.number(),
    invite_url: nullableString,
    expires_at: nullableString,
    max_uses: nullableNumber,
    used_count: z.number().default(0),
    created_at: nullableString,
    creator: adminUserSchema.nullable().optional(),
  })
  .passthrough()

const invitesResponseSchema = z
  .object({
    poule_id: z.number(),
    data: z.array(inviteSchema).default([]),
  })
  .passthrough()

export type PouleAdminOverview = z.infer<typeof adminOverviewSchema>
export type PouleAdminMember = z.infer<typeof adminMemberSchema>
export type PouleAdminMembersResponse = z.infer<typeof adminMembersResponseSchema>
export type PouleAdminCommunicationPreview = z.infer<typeof communicationPreviewSchema>
export type PouleAdminCommunication = z.infer<typeof communicationSchema>
export type PouleAdminCommunicationsResponse = z.infer<typeof communicationsResponseSchema>
export type PouleAdminSettings = z.infer<typeof adminSettingsSchema>
export type PouleAdminAuditEvent = z.infer<typeof auditEventSchema>
export type PouleAdminAuditResponse = z.infer<typeof auditResponseSchema>
export type PouleMemberImport = z.infer<typeof memberImportSchema>
export type PouleAdminCorrectionMatch = z.infer<typeof correctionMatchSchema>
export type PouleAdminInvite = z.infer<typeof inviteSchema>

async function fetchPouleAdminOverview(pouleId: number): Promise<PouleAdminOverview> {
  const response = await api.get<unknown>(`/poules/${pouleId}/admin/overview`)
  const parsed = adminOverviewSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poule-admin] Invalid overview payload', parsed.error.flatten())
    throw new Error('Poulebeheer kon niet worden geladen.')
  }

  return parsed.data
}

async function fetchPouleAdminMembers(
  pouleId: number,
  status: string,
  query: string,
  page: number,
): Promise<PouleAdminMembersResponse> {
  const params = new URLSearchParams({
    status,
    page: String(page),
    per_page: '25',
  })
  if (query.trim()) params.set('q', query.trim())

  const response = await api.get<unknown>(`/poules/${pouleId}/admin/members?${params.toString()}`)
  const parsed = adminMembersResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poule-admin] Invalid members payload', parsed.error.flatten())
    throw new Error('Deelnemers konden niet worden geladen.')
  }

  return parsed.data
}

export function usePouleAdminOverview(pouleId: number | null) {
  return useQuery({
    queryKey: ['poule-admin-overview', pouleId],
    queryFn: () => fetchPouleAdminOverview(pouleId as number),
    enabled: pouleId != null,
    staleTime: 30_000,
  })
}

export function usePouleAdminMembers(
  pouleId: number | null,
  status: string,
  query: string,
  enabled = true,
) {
  const normalizedQuery = query.trim()

  return useInfiniteQuery({
    queryKey: ['poule-admin-members', pouleId, status, normalizedQuery],
    queryFn: ({ pageParam }) => fetchPouleAdminMembers(
      pouleId as number,
      status,
      normalizedQuery,
      Number(pageParam),
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined,
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

type PouleAdminMemberAction = 'approve' | 'decline' | 'remove'

export function usePouleAdminMemberAction(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, action }: { userId: number; action: PouleAdminMemberAction }) => {
      const path = action === 'remove'
        ? `/poules/${pouleId}/members/${userId}`
        : `/poules/${pouleId}/members/${userId}/${action}`

      if (action === 'remove') {
        return api.delete<unknown>(path)
      }

      return api.post<unknown>(path, {})
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-members', pouleId] }),
      ])
    },
  })
}

export function usePouleAdminMemberRole(pouleId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: 'member' | 'admin' }) => {
      return api.patch<unknown>(`/poules/${pouleId}/admin/members/${userId}/role`, { role })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-members', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })
}

type CommunicationPayload = {
  subject: string
  body: string
  locale: string
}

export function usePouleAdminCommunicationPreview(pouleId: number | null) {
  return useMutation({
    mutationFn: async (payload: CommunicationPayload) => {
      const response = await api.post<unknown>(`/poules/${pouleId}/admin/communications/preview`, payload)
      const parsed = communicationPreviewSchema.safeParse(response)

      if (!parsed.success) {
        throw new Error('De e-mailpreview kon niet worden geladen.')
      }

      return parsed.data
    },
  })
}

export function usePouleAdminCommunicationSend(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CommunicationPayload & { idempotency_key: string }) => {
      const response = await api.post<unknown>(`/poules/${pouleId}/admin/communications/send`, payload)
      const parsed = communicationSchema.safeParse(response)

      if (!parsed.success) {
        throw new Error('De e-mail kon niet in de wachtrij worden geplaatst.')
      }

      return parsed.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['poule-admin-communications', pouleId] })
      await queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] })
    },
  })
}

export function usePouleAdminCommunications(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-admin-communications', pouleId],
    queryFn: async () => {
      const response = await api.get<unknown>(`/poules/${pouleId}/admin/communications`)
      const parsed = communicationsResponseSchema.safeParse(response)

      if (!parsed.success) {
        throw new Error('De eerdere berichten konden niet worden geladen.')
      }

      return parsed.data
    },
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function usePouleAdminSettings(pouleId: number | null) {
  return useQuery({
    queryKey: ['poule-admin-settings', pouleId],
    queryFn: async () => {
      const response = await api.get<unknown>(`/poules/${pouleId}/admin/settings`)
      const parsed = adminSettingsSchema.safeParse(response)
      if (!parsed.success) throw new Error('De poule-instellingen konden niet worden geladen.')
      return parsed.data
    },
    enabled: pouleId != null,
    staleTime: 30_000,
  })
}

export function useUpdatePouleAdminSettings(pouleId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Pick<PouleAdminSettings, 'name' | 'description' | 'visibility' | 'requires_approval' | 'prediction_release_mode' | 'show_in_overviews'>>) => {
      const response = await api.patch<unknown>(`/poules/${pouleId}/admin/settings`, payload)
      const parsed = adminSettingsSchema.safeParse(response)
      if (!parsed.success) throw new Error('De poule-instellingen konden niet worden opgeslagen.')
      return parsed.data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-settings', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })
}

export function useDeletePoule(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (pouleId == null) throw new Error('Ongeldige poule.')
      return api.delete<unknown>(`/poules/${pouleId}`)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['joined-poules'] }),
        queryClient.removeQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.removeQueries({ queryKey: ['poule-admin-settings', pouleId] }),
      ])
    },
  })
}

export function usePouleAdminAudit(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-admin-audit', pouleId],
    queryFn: async () => {
      const response = await api.get<unknown>(`/poules/${pouleId}/admin/audit`)
      const parsed = auditResponseSchema.safeParse(response)
      if (!parsed.success) throw new Error('Het activiteitenlogboek kon niet worden geladen.')
      return parsed.data
    },
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function usePreviewPouleMemberImport(pouleId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)
      const response = await api.post<unknown>(`/poules/${pouleId}/admin/imports/members/preview`, body)
      const parsed = memberImportSchema.safeParse(response)
      if (!parsed.success) throw new Error('De CSV-preview kon niet worden geladen.')
      return parsed.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] })
    },
  })
}

export function useCommitPouleMemberImport(pouleId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ importId, idempotencyKey }: { importId: number; idempotencyKey: string }) => {
      const response = await api.post<unknown>(`/poules/${pouleId}/admin/imports/${importId}/commit`, { idempotency_key: idempotencyKey })
      const parsed = memberImportSchema.safeParse(response)
      if (!parsed.success) throw new Error('De CSV-import kon niet worden verwerkt.')
      return parsed.data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-members', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })
}

export function usePouleAdminCorrections(pouleId: number | null, userId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-admin-corrections', pouleId, userId],
    queryFn: async () => {
      const response = await api.get<unknown>(`/poules/${pouleId}/admin/corrections/predictions?user_id=${userId}`)
      const parsed = correctionResponseSchema.safeParse(response)
      if (!parsed.success) throw new Error('De voorspellingen konden niet worden geladen.')
      return parsed.data
    },
    enabled: pouleId != null && userId != null && enabled,
    staleTime: 15_000,
  })
}

export function useUpdatePouleAdminCorrection(pouleId: number | null, userId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ matchId, homePred, awayPred }: { matchId: number; homePred: number; awayPred: number }) => {
      return api.post<unknown>(`/poules/${pouleId}/admin/corrections/predictions/${userId}/${matchId}`, {
        home_pred: homePred,
        away_pred: awayPred,
      })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-corrections', pouleId, userId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })
}

export function usePouleAdminInvites(pouleId: number | null, enabled = true) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['poule-admin-invites', pouleId],
    queryFn: async () => {
      const response = await api.get<unknown>(`/poules/${pouleId}/admin/invites`)
      const parsed = invitesResponseSchema.safeParse(response)
      if (!parsed.success) throw new Error('De uitnodigingen konden niet worden geladen.')
      return parsed.data
    },
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
  const create = useMutation({
    mutationFn: async (payload: { expires_at?: string; max_uses?: number }) => {
      const response = await api.post<unknown>(`/poules/${pouleId}/admin/invites`, payload)
      const parsed = inviteSchema.safeParse(response)
      if (!parsed.success) throw new Error('De uitnodiging kon niet worden aangemaakt.')
      return parsed.data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-invites', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })
  const revoke = useMutation({
    mutationFn: async (inviteId: number) => api.delete<unknown>(`/poules/${pouleId}/admin/invites/${inviteId}`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-admin-invites', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-overview', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['poule-admin-audit', pouleId] }),
      ])
    },
  })

  return { query, create, revoke }
}
